import React, { useMemo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState } from '../../../stores/types/types';
import { showAlert } from '../../general-components/alert-service';
import Socket from '../../../connection/socket';
import * as ShotsActions from '../../../stores/actions/shots';
import * as ModalActions from '../../../stores/actions/modal';
import { TeleprompterInterface, TeleprompterSectionInterface } from '../../../types/interfaces';
import useTeleprompters from '../../../hooks/use-teleprompters';
import MultiButtonConfirmationModal from './multi-button-confirmation-modal';
import useMetaMenuDrawer from '../../../hooks/use-meta-menu-drawer';

interface Props {
	socket: Socket
}

export default function DeleteShotConfirmation(props: Props) {
	const dispatch = useDispatch();
	const { socket } = props;
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);
	const currentShotId = useSelector((state: GreenroomState) => state.ShotsReducer.currentShotId);
	const shotModalShot = useSelector((state: GreenroomState) => state.ShotsReducer.shotModalShot);
	const teleprompters = useSelector(
		(state: GreenroomState) => state.TeleprompterReducer.teleprompters,
	);
	const telepromptersLoaded = useSelector(
		(state: GreenroomState) => state.TeleprompterReducer.telepromptersLoaded,
	);
	const activeTab = useSelector((state: GreenroomState) => state.TabsReducer.activeTab);

	const [deleteBodyText, setDeleteBodyText] = useState<string>('');
	const [deleteHeaderText, setHeaderText] = useState<string>('');

	const { loadTeleprompters } = useTeleprompters(socket);
	const { closeMetaMenuAndDrawer } = useMetaMenuDrawer();

	const shotHasTeleprompterNotes = useMemo(() => teleprompters
		.map(((teleprompter: TeleprompterInterface) => teleprompter.sections))
		.flat()
		.some((section: TeleprompterSectionInterface) => (
			section.section_type_id === shotModalShot.shot_id
			&& section.section_data.hasContent
		)), [shotModalShot, teleprompters]);

	useEffect(() => {
		if (token && !telepromptersLoaded) { loadTeleprompters(); }
	}, [token, telepromptersLoaded]);

	useEffect(() => {
		const shotTitle = shotModalShot.shot_data.shotTitle
			? shotModalShot.shot_data.shotTitle
			: 'Unnamed Shot';

		if (shotHasTeleprompterNotes && currentShotId === shotModalShot.shot_id) {
			setHeaderText('Are you sure you want to delete the LIVE shot and its Shot Notes?');
			setDeleteBodyText('You will lose the Shot Notes created for this shot, and your show will transition to the next available shot.');
		} else if (shotHasTeleprompterNotes) {
			setHeaderText(`Are you sure you want to delete shot, "${shotTitle}" and its Shot Notes?`);
			setDeleteBodyText('Once deleted, the shot and its Shot Notes cannot be recovered.');
		} else if (currentShotId === shotModalShot.shot_id) {
			setHeaderText('Are you sure you want to delete the LIVE shot?');
			setDeleteBodyText('This will cause your show to transition to the next available shot.');
		} else {
			setHeaderText(`Are you sure you want to delete shot, "${shotTitle}"?`);
			setDeleteBodyText('Once deleted, the shot cannot be recovered.');
		}
	}, [shotHasTeleprompterNotes, currentShotId, shotModalShot]);

	const deleteShot = () => {
		if (!socket || !token || !meeting) return;

		if (shots && shots.length === 1) {
			showAlert({
				message: 'You can’t do that',
				description: 'You need at least 1 shot in the showboard',
				duration: 5000,
				type: 'neutral',
			});
			return;
		}

		dispatch(ShotsActions.deleteShot(
			token,
			shotModalShot.shot_id,
			meeting.meeting,
		));
		if (currentShotId === shotModalShot.shot_id) {
			closeMetaMenuAndDrawer(activeTab);
		}
		dispatch(ModalActions.setModalOpen(false));
	};

	const closeModal = () => {
		dispatch(ModalActions.setModalOpen(false));
	};

	return (
		<MultiButtonConfirmationModal
			headerText={deleteHeaderText}
			bodyText={deleteBodyText}
			firstButtonText={shotHasTeleprompterNotes ? 'Delete Shot & Notes' : 'Delete Shot'}
			secondButtonText="Cancel"
			firstButtonAction={deleteShot}
			secondButtonAction={closeModal}
			isOpen
		/>
	);
}
